import { chartCalendarGetIntensityPercentile, type VxChartCalendarIntensityLevel } from '@voxie/frontend-components';
import { createGlobalState } from '@vueuse/core';
import type { Dayjs } from 'dayjs';
import { ref } from 'vue';
import type { SenderType } from '~/constants/senderTypes';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import { fetchReservedDatesInRange } from '~/services/reservedDates/fetchReservedDatesInRange';
import type { ClientContext } from '~/services/contextClient';
import { getClientTz } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { catchRequestError } from '~/utils/request';

export type BlastForecastItemAggregated = {
    date: Date;
    num_corporate: number;
    num_org_units: number;
    num_groups: number;
    num_messages: number;
    num_blasts: number;
    estimated_cost: number;
    intensity: VxChartCalendarIntensityLevel;
    cost_intensity: VxChartCalendarIntensityLevel;
    reserved_date_id?: number;
    reserved_date_reason?: string | null;
};

export const useBlastForecastSummary = createGlobalState(() => {
    const data = ref<BlastForecastItemAggregated[]>([]);
    const loading = ref(false);
    const error = ref<string>();

    const calculateIntensity = () => {
        const sortedNumMessages = [...new Set(data.value.map((item) => item.num_messages).filter(Boolean))].sort(
            (a, b) => a - b
        );
        const sortedEstimatedCosts = [...new Set(data.value.map((item) => item.estimated_cost).filter(Boolean))].sort(
            (a, b) => a - b
        );

        data.value.forEach((day) => {
            day.intensity = chartCalendarGetIntensityPercentile(sortedNumMessages, day.num_messages);
            day.cost_intensity = chartCalendarGetIntensityPercentile(sortedEstimatedCosts, day.estimated_cost);
        });
    };

    const fillAggregatedWithEmptyData = (
        fetchParams: GetBlastForecastRequest,
        aggregatedByDay: Map<string, BlastForecastItemAggregated>
    ) => {
        const [, endDate] = fetchParams.range;
        const today: Dayjs = dayjs().tz(getClientTz());
        const endDayjs: Dayjs = dayjs.utc(endDate).tz(getClientTz());

        let currentDate = today.startOf('day');
        while (currentDate.isSameOrBefore(endDayjs, 'day')) {
            const dateKey = currentDate.format('YYYY-MM-DD');
            aggregatedByDay.set(dateKey, {
                date: currentDate.toDate(),
                num_corporate: 0,
                num_org_units: 0,
                num_groups: 0,
                num_messages: 0,
                num_blasts: 0,
                estimated_cost: 0,
                intensity: 'default',
                cost_intensity: 'default',
            });
            currentDate = currentDate.add(1, 'day');
        }
    };

    const fetchData = async (clientContext: ClientContext, fetchParams: GetBlastForecastRequest) => {
        loading.value = true;
        error.value = undefined;

        try {
            const [startDate, endDate] = fetchParams.range;

            const formattedStart = dayjs.utc(startDate).tz(getClientTz()).format('YYYY-MM-DD');
            const formattedEnd = dayjs.utc(endDate).tz(getClientTz()).format('YYYY-MM-DD');

            const [response, reservedDates] = await Promise.all([
                blastForecastClient.getBlastForecastSummary(clientContext, fetchParams),
                fetchReservedDatesInRange(clientContext.teamId, formattedStart, formattedEnd),
            ]);

            const reservedDateMap = new Map(reservedDates.map((rd) => [rd.date, { id: rd.id, reason: rd.reason }]));

            const aggregatedByDay = new Map<string, BlastForecastItemAggregated>();

            fillAggregatedWithEmptyData(fetchParams, aggregatedByDay);

            response.data.items.forEach((item) => {
                const dateKey = dayjs.utc(item.live_at).tz(getClientTz()).format('YYYY-MM-DD');
                const existing = aggregatedByDay.get(dateKey);

                if (existing) {
                    existing.num_corporate += item.sender_type === 'corporate' ? item.num_blasts : 0;
                    existing.num_org_units += item.sender_type === 'org_unit' ? item.num_blasts : 0;
                    existing.num_groups += item.sender_type === 'group' ? item.num_blasts : 0;
                    existing.num_messages += item.num_messages;
                    existing.num_blasts += item.num_blasts;
                    existing.estimated_cost += item.estimated_cost ?? 0;
                    // intensity is defined after aggregation so we can use minValue and maxValue
                    existing.intensity = 'default';
                } else {
                    aggregatedByDay.set(dateKey, {
                        date: dayjs.utc(item.live_at).tz(getClientTz()).startOf('day').toDate(),
                        num_corporate: item.sender_type === 'corporate' ? 1 : 0,
                        num_org_units: item.sender_type === 'org_unit' ? 1 : 0,
                        num_groups: item.sender_type === 'group' ? 1 : 0,
                        num_messages: item.num_messages,
                        num_blasts: item.num_blasts,
                        estimated_cost: item.estimated_cost ?? 0,
                        // intensity is defined after aggregation so we can use minValue and maxValue
                        intensity: 'default',
                        cost_intensity: 'default',
                    });
                }
            });

            aggregatedByDay.forEach((dayData, dateKey) => {
                const reservedDate = reservedDateMap.get(dateKey);
                dayData.reserved_date_id = reservedDate?.id;
                dayData.reserved_date_reason = reservedDate?.reason;
            });

            data.value = Array.from(aggregatedByDay.values());
            calculateIntensity();
        } catch (e) {
            error.value = catchRequestError(e);
            data.value = [];
        } finally {
            loading.value = false;
        }
    };

    const reset = () => {
        data.value = [];
        loading.value = false;
        error.value = undefined;
    };

    const subtractCampaignFromDay = (
        date: Date,
        senderType: SenderType,
        audienceSize: number,
        estimatedCost: number | null
    ) => {
        const dateKey = dayjs(date).format('YYYY-MM-DD');
        const dayData = data.value.find((item) => dayjs(item.date).format('YYYY-MM-DD') === dateKey);

        if (dayData) {
            dayData.num_messages -= audienceSize;
            dayData.estimated_cost -= estimatedCost ?? 0;
            dayData.num_blasts -= 1;

            switch (senderType) {
                case 'corporate':
                    dayData.num_corporate -= 1;
                    break;
                case 'org_unit':
                    dayData.num_org_units -= 1;
                    break;
                case 'group':
                    dayData.num_groups -= 1;
                    break;
            }

            calculateIntensity();
        }
    };

    const setDayReservedDateId = (date: Date, reservedDateId?: number, reservedDateReason?: string | null) => {
        const dateKey = dayjs(date).format('YYYY-MM-DD');
        const dayData = data.value.find((item) => dayjs(item.date).format('YYYY-MM-DD') === dateKey);

        if (dayData) {
            dayData.reserved_date_id = reservedDateId;
            dayData.reserved_date_reason = reservedDateReason;
        }
    };

    return {
        data,
        loading,
        error,
        fetchData,
        reset,
        subtractCampaignFromDay,
        setDayReservedDateId,
    };
});
